import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Loader2, MoreHorizontal } from "lucide-react";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";

interface PopulatedRef {
  _id: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
}

interface AutomationRunRow {
  _id: string;
  status: "active" | "completed" | "waiting" | "error";
  automation: PopulatedRef | string;
  lead: PopulatedRef | string;
  currentStepId?: string;
  errorMsg?: string;
  createdAt: string;
  updatedAt: string;
}

function leadLabel(lead: AutomationRunRow["lead"]): string {
  if (lead && typeof lead === "object") {
    const parts = [lead.firstName, lead.lastName].filter(Boolean);
    if (parts.length > 0) return parts.join(" ");
    if (lead.email) return lead.email;
    if (lead.phoneNumber) return lead.phoneNumber;
  }
  return "—";
}

function automationName(automation: AutomationRunRow["automation"]): string {
  if (automation && typeof automation === "object" && automation.name) {
    return automation.name;
  }
  return "—";
}

export function AutomationRuns() {
  const navigate = useNavigate();
  const [runs, setRuns] = useState<AutomationRunRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();

  useEffect(() => {
    async function fetchRuns() {
      setIsLoading(true);
      try {
        const response = await fetch("/api/automation-runs");
        if (response.ok) {
          const data = await response.json();
          setRuns(data.runs || []);
        } else {
          console.error("Failed to fetch automation runs");
        }

        setBreadcrumbs([
          { label: "Flows", path: "/automations" },
          { label: "Runs", path: "/automations/runs" },
        ]);
      } catch (e) {
        console.error("Failed to fetch automation runs:", e);
      } finally {
        setIsLoading(false);
      }
    }

    fetchRuns();

    return () => {
      clearBreadcrumbs();
    };
  }, []);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const handleRetry = async ({ runId }: { runId: string }) => {
    setRetryingId(runId);
    try {
      const response = await fetch(`/api/automation-runs/${runId}/retry`, {
        method: "POST",
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        alert(data.error || "Failed to notify worker");
      }
    } catch (e) {
      console.error(e);
      alert("Failed to notify worker");
    } finally {
      setRetryingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <Loader2 className="animate-spin h-12 w-12 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Flow</TableHead>
            <TableHead>Contact</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Updated</TableHead>
            <TableHead className="w-12" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {runs.length > 0 ? (
            runs.map((run) => (
              <TableRow
                key={run._id}
                className="hover:bg-muted/50 cursor-pointer"
                onClick={() => navigate(`/automations/runs/${run._id}`)}
              >
                <TableCell>{automationName(run.automation)}</TableCell>
                <TableCell>{leadLabel(run.lead)}</TableCell>
                <TableCell className="capitalize">{run.status}</TableCell>
                <TableCell>{formatDate(run.updatedAt)}</TableCell>
                <TableCell
                  className="p-2 text-right"
                  onClick={(e) => e.stopPropagation()}
                >
                  {run.status === "active" ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          aria-label="Run actions"
                          disabled={retryingId === run._id}
                        >
                          {retryingId === run._id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <MoreHorizontal className="h-4 w-4" />
                          )}
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          disabled={retryingId === run._id}
                          onClick={() => handleRetry({ runId: run._id })}
                        >
                          Retry now
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : (
                    <span className="inline-block w-8" />
                  )}
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={5} className="h-24 text-center">
                No flow runs yet.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

export function AutomationRunView() {
  const { id } = useParams<{ id: string }>();
  const [run, setRun] = useState<Record<string, unknown> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();

  useEffect(() => {
    async function fetchRun() {
      if (!id) return;

      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(`/api/automation-runs/${id}`);
        if (response.ok) {
          const data = await response.json();
          setRun(data.run ?? null);
        } else {
          const data = await response.json().catch(() => ({}));
          setError(data.error || "Failed to fetch flow run");
        }

        setBreadcrumbs([
          { label: "Flows", path: "/automations" },
          { label: "Runs", path: "/automations/runs" },
          { label: id, path: `/automations/runs/${id}` },
        ]);
      } catch (e) {
        console.error("Failed to fetch automation run:", e);
        setError("Failed to fetch flow run");
      } finally {
        setIsLoading(false);
      }
    }

    fetchRun();

    return () => {
      clearBreadcrumbs();
    };
  }, [id]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <Loader2 className="animate-spin h-12 w-12 text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return <div className="text-destructive">{error}</div>;
  }

  return (
    <pre className="p-4 bg-muted rounded-md overflow-auto text-sm">
      {JSON.stringify(run, null, 2)}
    </pre>
  );
}
